import { useState } from 'react';
import {
  Box, Paper, BottomNavigation, BottomNavigationAction, Card, CardContent, Typography, Stack, Button, Chip, Divider,
  TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Snackbar, FormControlLabel, Switch, IconButton, List, ListItem, ListItemText, LinearProgress,
} from '@mui/material';
import { Home, Assignment, AccessTime, CloudSync, MyLocation, PhotoCamera, Login, Logout, Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { fieldApi, type AssignedWorkOrder, type PhotoMeta, type SyncAction } from '../../api/field.api';
import { warehousesApi, itemsApi } from '../../api/inventory.api';
import { extractApiError } from '../../api/error';

const QUEUE_KEY = 'gf_field_offline_queue';
const round = (n: number) => Math.round(n * 1e7) / 1e7;
const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `q-${Date.now()}-${Math.random().toString(36).slice(2)}`);

function getPosition(): Promise<{ lat: number; lng: number; accuracy?: number }> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ lat: 28.6139, lng: 77.209 });
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: round(p.coords.latitude), lng: round(p.coords.longitude), accuracy: Math.round(p.coords.accuracy) }),
      () => resolve({ lat: 28.6139, lng: 77.209 }),
      { timeout: 4000 },
    );
  });
}
const loadQueue = (): SyncAction[] => { try { return JSON.parse(localStorage.getItem(QUEUE_KEY) ?? '[]'); } catch { return []; } };
const saveQueue = (q: SyncAction[]) => localStorage.setItem(QUEUE_KEY, JSON.stringify(q));

export function FieldMobilePage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState(0);
  const [offline, setOffline] = useState(false);
  const [queue, setQueue] = useState<SyncAction[]>(loadQueue());
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeWo, setActiveWo] = useState<AssignedWorkOrder | null>(null);
  const [dialog, setDialog] = useState<'checkin' | 'progress' | 'consumption' | null>(null);
  const [gps, setGps] = useState<{ lat: number; lng: number; accuracy?: number } | null>(null);
  const [photos, setPhotos] = useState<PhotoMeta[]>([]);
  const [progressVal, setProgressVal] = useState(50);
  const [consume, setConsume] = useState({ itemId: '', warehouseId: '', quantity: 0 });

  const dashboard = useQuery({ queryKey: ['field', 'dashboard'], queryFn: () => fieldApi.dashboard(), enabled: tab === 0 });
  const tasks = useQuery({ queryKey: ['field', 'tasks'], queryFn: () => fieldApi.workOrders({ limit: 50 }), enabled: tab === 1 });
  const attendance = useQuery({ queryKey: ['field', 'attendance'], queryFn: () => fieldApi.attendance({ limit: 30 }), enabled: tab === 2 });
  const warehouses = useQuery({ queryKey: ['warehouses', 'all'], queryFn: () => warehousesApi.list({ limit: 100 }), enabled: dialog === 'consumption' });
  const items = useQuery({ queryKey: ['inventory-items', 'all'], queryFn: () => itemsApi.list({ limit: 200 }), enabled: dialog === 'consumption' });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['field'] }); };

  const enqueue = (action: Omit<SyncAction, 'clientRef'>) => { const next = [...queue, { ...action, clientRef: uuid() }]; setQueue(next); saveQueue(next); setToast('Saved offline — will sync later'); };

  const run = async (type: SyncAction['type'], payload: Record<string, unknown>, visitId?: number) => {
    if (offline) { enqueue({ type, payload, visitId }); return; }
    try {
      if (type === 'checkin') await fieldApi.checkin(payload as never);
      else if (type === 'progress') await fieldApi.progress(payload as never);
      else if (type === 'consumption') await fieldApi.consumption(payload as never);
      else if (type === 'attendance_checkin') await fieldApi.attendanceCheckin(payload as never);
      else if (type === 'attendance_checkout') await fieldApi.attendanceCheckout(payload as never);
      setToast('Done'); invalidate();
    } catch (e) { setError(extractApiError(e)); }
  };

  const sync = useMutation({
    mutationFn: () => fieldApi.sync(queue),
    onSuccess: (r) => { setQueue([]); saveQueue([]); setToast(`Synced: ${r.applied} applied, ${r.duplicates} duplicate, ${r.failed} failed`); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });

  const captureGps = async () => setGps(await getPosition());
  const addPhoto = () => setPhotos((p) => [...p, { fileName: `photo-${p.length + 1}.jpg`, fileUrl: `https://cdn.gigforce/field/${uuid()}.jpg`, mimeType: 'image/jpeg', sizeBytes: 250000, lat: gps?.lat, lng: gps?.lng, capturedAt: new Date().toISOString() }]);
  const openDialog = async (wo: AssignedWorkOrder, kind: 'checkin' | 'progress' | 'consumption') => { setActiveWo(wo); setPhotos([]); setGps(null); setError(null); if (kind !== 'consumption') await captureGps(); setDialog(kind); };

  const submitDialog = async () => {
    if (!activeWo) return;
    if (dialog === 'checkin') await run('checkin', { workOrderId: activeWo.id, siteId: activeWo.siteId, lat: gps?.lat ?? 0, lng: gps?.lng ?? 0, accuracy: gps?.accuracy, photos });
    else if (dialog === 'progress') await run('progress', { workOrderId: activeWo.id, siteId: activeWo.siteId, percentComplete: progressVal, photos });
    else if (dialog === 'consumption') await run('consumption', { workOrderId: activeWo.id, siteId: activeWo.siteId, itemId: Number(consume.itemId), warehouseId: Number(consume.warehouseId), quantity: consume.quantity });
    setDialog(null);
  };

  const d = dashboard.data;

  return (
    <>
      <PageHeader
        title="Field Force — Mobile Preview"
        subtitle="Responsive field workflow as it appears on a technician/vendor device. APIs are mobile-ready; offline actions queue locally."
        action={<FormControlLabel control={<Switch checked={offline} onChange={(e) => setOffline(e.target.checked)} color="warning" />} label={offline ? 'Offline' : 'Online'} />}
      />

      {/* Phone frame */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Paper elevation={6} sx={{ width: 400, maxWidth: '100%', height: 720, borderRadius: 5, overflow: 'hidden', display: 'flex', flexDirection: 'column', border: '8px solid #111', position: 'relative' }}>
          <Box sx={{ bgcolor: 'primary.main', color: '#fff', px: 2, py: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={700}>MyevCRM Field</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>{offline ? '⚠ Offline — queue active' : '● Online'}</Typography>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, bgcolor: 'background.default' }}>
            {tab === 0 && (
              <Stack spacing={1.5}>
                {dashboard.isLoading && <LinearProgress />}
                <Stack direction="row" spacing={1}>
                  <Card sx={{ flex: 1 }}><CardContent sx={{ py: 1.5 }}><Typography variant="overline" color="text.secondary">Assigned</Typography><Typography variant="h5">{d?.assignedWorkOrders.total ?? 0}</Typography></CardContent></Card>
                  <Card sx={{ flex: 1 }}><CardContent sx={{ py: 1.5 }}><Typography variant="overline" color="text.secondary">Open Visits</Typography><Typography variant="h5">{d?.openVisits ?? 0}</Typography></CardContent></Card>
                </Stack>
                <Card><CardContent sx={{ py: 1.5 }}>
                  <Typography variant="subtitle2">Today's Attendance</Typography>
                  {d?.attendanceToday ? <StatusBadge status={d.attendanceToday.status} /> : <Typography variant="body2" color="text.secondary">Not checked in</Typography>}
                </CardContent></Card>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>Recent Visits</Typography>
                {(d?.recentVisits ?? []).map((v) => (
                  <Card key={v.id}><CardContent sx={{ py: 1 }}><Stack direction="row" justifyContent="space-between" alignItems="center"><Box><Typography variant="body2">{v.code}</Typography><Typography variant="caption" color="text.secondary">WO #{v.workOrderId ?? '—'}</Typography></Box><StatusBadge status={v.status} /></Stack></CardContent></Card>
                ))}
                {(d?.recentVisits.length ?? 0) === 0 && <Typography variant="body2" color="text.secondary">No visits yet.</Typography>}
              </Stack>
            )}

            {tab === 1 && (
              <Stack spacing={1.5}>
                {tasks.isLoading && <LinearProgress />}
                {(tasks.data?.data ?? []).map((wo) => (
                  <Card key={wo.id}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="subtitle2">{wo.code}</Typography><StatusBadge status={wo.status} />
                      </Stack>
                      <Typography variant="body2" gutterBottom>{wo.title}</Typography>
                      <LinearProgress variant="determinate" value={wo.percentComplete} sx={{ mb: 1, borderRadius: 1 }} />
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button size="small" variant="outlined" startIcon={<MyLocation />} onClick={() => openDialog(wo, 'checkin')}>Check-in</Button>
                        <Button size="small" variant="outlined" onClick={() => { setProgressVal(wo.percentComplete); openDialog(wo, 'progress'); }}>Progress</Button>
                        <Button size="small" variant="outlined" onClick={() => openDialog(wo, 'consumption')}>Material</Button>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
                {(tasks.data?.data.length ?? 0) === 0 && !tasks.isLoading && <Typography variant="body2" color="text.secondary">No assigned tasks.</Typography>}
              </Stack>
            )}

            {tab === 2 && (
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1}>
                  <Button fullWidth variant="contained" startIcon={<Login />} onClick={async () => { const g = await getPosition(); await run('attendance_checkin', { lat: g.lat, lng: g.lng }); }}>Check-in</Button>
                  <Button fullWidth variant="outlined" startIcon={<Logout />} onClick={async () => { const g = await getPosition(); await run('attendance_checkout', { lat: g.lat, lng: g.lng }); }}>Check-out</Button>
                </Stack>
                {attendance.isLoading && <LinearProgress />}
                <List dense>
                  {(attendance.data ?? []).map((a) => (
                    <ListItem key={a.id} divider secondaryAction={<StatusBadge status={a.status} />}>
                      <ListItemText primary={a.attendance_date} secondary={`${a.check_in_at ? new Date(a.check_in_at).toLocaleTimeString() : '—'} → ${a.check_out_at ? new Date(a.check_out_at).toLocaleTimeString() : '—'} · ${a.total_hours ?? 0}h`} />
                    </ListItem>
                  ))}
                </List>
                {(attendance.data?.length ?? 0) === 0 && !attendance.isLoading && <Typography variant="body2" color="text.secondary">No attendance records.</Typography>}
              </Stack>
            )}

            {tab === 3 && (
              <Stack spacing={1.5}>
                <Alert severity={queue.length ? 'warning' : 'success'}>{queue.length ? `${queue.length} action(s) queued offline` : 'Queue is empty — all synced'}</Alert>
                <Button variant="contained" startIcon={<CloudSync />} disabled={queue.length === 0 || sync.isPending} onClick={() => sync.mutate()}>Sync now</Button>
                <List dense>
                  {queue.map((a, i) => (
                    <ListItem key={a.clientRef} divider secondaryAction={<IconButton size="small" onClick={() => { const next = queue.filter((_, idx) => idx !== i); setQueue(next); saveQueue(next); }}><Delete fontSize="small" /></IconButton>}>
                      <ListItemText primary={a.type.replace(/_/g, ' ')} secondary={a.clientRef.slice(0, 8)} />
                    </ListItem>
                  ))}
                </List>
              </Stack>
            )}
          </Box>

          <BottomNavigation showLabels value={tab} onChange={(_e, v) => setTab(v)} sx={{ borderTop: 1, borderColor: 'divider' }}>
            <BottomNavigationAction label="Home" icon={<Home />} />
            <BottomNavigationAction label="Tasks" icon={<Assignment />} />
            <BottomNavigationAction label="Attendance" icon={<AccessTime />} />
            <BottomNavigationAction label={queue.length ? `Sync (${queue.length})` : 'Sync'} icon={<CloudSync />} />
          </BottomNavigation>
        </Paper>
      </Box>

      {/* Action dialog */}
      <Dialog open={dialog !== null} onClose={() => setDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ textTransform: 'capitalize' }}>{dialog} — {activeWo?.code}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            {(dialog === 'checkin') && (
              <>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button size="small" startIcon={<MyLocation />} onClick={captureGps}>Capture GPS</Button>
                  {gps && <Chip size="small" label={`${gps.lat}, ${gps.lng}`} />}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button size="small" startIcon={<PhotoCamera />} onClick={addPhoto}>Attach photo</Button>
                  <Chip size="small" label={`${photos.length} photo(s)`} />
                </Stack>
                <Divider />
                <Typography variant="caption" color="text.secondary">Check-in captures latitude, longitude, timestamp and photo metadata.</Typography>
              </>
            )}
            {dialog === 'progress' && (
              <>
                <Typography variant="body2">Progress: {progressVal}%</Typography>
                <input type="range" min={0} max={100} value={progressVal} onChange={(e) => setProgressVal(Number(e.target.value))} />
                <Typography variant="caption" color="text.secondary">Updates the work order's completion percentage.</Typography>
              </>
            )}
            {dialog === 'consumption' && (
              <>
                <TextField select size="small" label="Item" value={consume.itemId} onChange={(e) => setConsume((c) => ({ ...c, itemId: e.target.value }))} fullWidth>
                  {(items.data?.data ?? []).map((i) => <MenuItem key={i.id} value={i.id}>{i.code} — {i.name}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Warehouse" value={consume.warehouseId} onChange={(e) => setConsume((c) => ({ ...c, warehouseId: e.target.value }))} fullWidth>
                  {(warehouses.data?.data ?? []).map((w) => <MenuItem key={w.id} value={w.id}>{w.code} — {w.name}</MenuItem>)}
                </TextField>
                <TextField size="small" type="number" label="Quantity" value={consume.quantity} onChange={(e) => setConsume((c) => ({ ...c, quantity: Number(e.target.value) }))} fullWidth />
                <Typography variant="caption" color="text.secondary">Posts an inventory issue linked to this work order.</Typography>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitDialog}>{offline ? 'Queue' : 'Submit'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
